import {
  Badge,
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  Icon,
  IconButton,
  Input,
  Td,
  Text,
  Tr,
} from "@chakra-ui/react";
import {
  AddIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DeleteIcon,
} from "@chakra-ui/icons";
// The same icon the sidebar gives the requirement library, so the action points somewhere recognisable.
import { FiBookOpen } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import type {
  AwardCriterionDto,
  SourcingEventDto,
  SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { CommentSubjectKind, WeightType } from "../../../../../autogen/bff-api";
import { ItemComments } from "./commentRound/ItemComments";
import {
  useEditor,
  useFieldErrorTranslation,
} from "../../../../../common/editing/save-coordinator";
import { rowOpenProps } from "../../../../../common/clickableRow";
import { priceModelType } from "../../../common/priceModel";
import {
  criterionStatus,
  subWeightTotal,
} from "../../../../../common/award-criteria/awardCriteriaStatus";
import { criterionTypeLabelKey } from "../../../../../common/procurement/publicProcurementOptions";
import { AwardStatusChip } from "./AwardStatusChip";
import { AutoResizeTextarea } from "../../../../../common/products/AutoResizeTextarea";
import { WeightInput } from "../../../../../common/award-criteria/WeightInput";

// t('Complete') t('No sub-criteria') t('Missing description') t('Sub-criterion weights {{total}} %')
// t('Delete criterion') t('Weight') t('Model: {{model}}') t('Criterion name')
// t('{{count}} sub-criterion') t('{{count}} sub-criterion_other')
// t('Expand {{criterion}}') t('Collapse {{criterion}}')
// t('Save to the requirement library') t('Already in the requirement library')
// t('Priority {{rank}}') t('Move {{criterion}} up') t('Move {{criterion}} down')

interface Props {
  eventId: string;
  index: number;
  criterion: AwardCriterionDto;
  isLocked: boolean;
  isPriceCriterion: boolean;
  priceModel: SourcingEventDto["eventData"]["priceModel"];
  priority?: number;
  inOrderOfPriority?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onChange: (next: AwardCriterionDto) => void;
  onOpen: () => void;
  onDelete: () => void;
  /** Whether this criterion's requirements and its add-requirement row are showing below it. */
  isExpanded: boolean;
  onToggleExpanded: () => void;
  /** Just added from the library — marked until the confirmation above the table is dismissed. */
  isMarked?: boolean;
  /** Keeps this criterion for reuse. Omitted when the library is off or the user may not write to it. */
  onSaveToLibrary?: () => void;
  /** The library already holds a criterion by this name, so there is nothing left to keep. */
  isInLibrary?: boolean;
  stacked?: boolean;
}

const LINE_BREAKS = /\s*[\r\n]+\s*/g;

export const AwardCriterionRow = ({
  eventId,
  index,
  criterion,
  isLocked,
  isPriceCriterion,
  priceModel,
  priority,
  inOrderOfPriority = false,
  onMoveUp,
  onMoveDown,
  onChange,
  onOpen,
  onDelete,
  isExpanded,
  onToggleExpanded,
  isMarked = false,
  onSaveToLibrary,
  isInLibrary = false,
  stacked = false,
}: Props) => {
  const { t } = useTranslation();
  const formatFieldError = useFieldErrorTranslation();
  const { fieldError } = useEditor<SourcingEventDto, SourcingEventPatchDto>(
    eventId,
  );
  const weightFieldError = fieldError(
    `eventData.awardCriteria.bestPriceQualityRatio.criteria.${index}.weight.number`,
  );

  const status = criterionStatus(criterion);
  const subCriteria = criterion.subCriteria ?? [];
  const criterionName =
    criterion.name?.trim() || t(criterionTypeLabelKey(criterion.type));

  // Only the collapsed state distinguishes the two: an arrow when there is something folded away, a
  // plus when the criterion has no requirements yet, since opening it is how the first one gets added.
  // Open, every row behaves alike — same content showing, same click to close it — so they read alike.
  const toggleGlyph = isExpanded
    ? "chevron-down"
    : subCriteria.length
      ? "chevron-right"
      : "add";
  const toggleIcon = {
    "chevron-down": <ChevronDownIcon />,
    "chevron-right": <ChevronRightIcon />,
    add: <AddIcon boxSize="2.5" />,
  }[toggleGlyph];

  const statusChip = (() => {
    switch (status.kind) {
      case "complete":
        return (
          <AwardStatusChip
            testId="award-criterion-status"
            tone="success"
            label={t("Complete")}
          />
        );
      case "no-sub-criteria":
        return (
          <AwardStatusChip
            testId="award-criterion-status"
            tone="neutral"
            label={t("No sub-criteria")}
          />
        );
      case "missing-description":
        return (
          <AwardStatusChip
            testId="award-criterion-status"
            tone="warning"
            label={t("Missing description")}
          />
        );
      case "sub-weights-invalid":
        return (
          <AwardStatusChip
            testId="award-criterion-status"
            tone="warning"
            label={t("Sub-criterion weights {{total}} %", {
              total: status.total,
            })}
          />
        );
    }
  })();

  const requirementSummary = (
    <Text
      fontSize="sm"
      color={status.kind === "sub-weights-invalid" ? "smWarning" : "smMuted"}
      data-testid="award-sub-weight-total"
      data-valid={status.kind !== "sub-weights-invalid"}
    >
      {subCriteria.length === 0
        ? "—"
        : status.kind === "sub-weights-invalid"
          ? t("{{count}} sub-criteria · weights {{total}} %", {
              count: subCriteria.length,
              total: subWeightTotal(criterion),
            })
          : t("{{count}} sub-criterion", { count: subCriteria.length })}
    </Text>
  );

  // A price row is scored from the tendered price rather than by an evaluator, so it names the model —
  // and keeps naming it once it has requirements, since those specify how the price weight is composed
  // without changing where the points come from.
  const typeSummaryCell = isPriceCriterion ? (
    <Flex align="center" gap="2" wrap="wrap">
      <Badge
        bg="smPrimarySofter"
        color="smPrimary"
        px="2"
        py="1"
        borderRadius="md"
        textTransform="none"
      >
        {t("Model: {{model}}", { model: t(priceModelType(priceModel)) })}
      </Badge>
      {subCriteria.length > 0 && requirementSummary}
    </Flex>
  ) : (
    requirementSummary
  );
  const summaryCell = (
    <Box>
      {typeSummaryCell}
      <ItemComments kind={CommentSubjectKind.AwardCriterion} itemRef={criterion.id} />
    </Box>
  );

  const changeName = (name: string) => onChange({ ...criterion, name });
  const nameFieldProps = {
    size: "sm",
    rounded: "md",
    flex: "1",
    minW: "0",
    fontWeight: "semibold",
    borderColor: "transparent",
    bg: "transparent",
    _hover: { borderColor: "smBorder" },
    autoComplete: "off",
    "data-1p-ignore": true,
    value: criterion.name ?? "",
    placeholder: t("Criterion name") ?? "",
    "aria-label": t("Criterion name") ?? "",
    isDisabled: isLocked,
  };

  const nameCell = (
    <Flex align={stacked ? "flex-start" : "center"} gap={stacked ? "2" : "3"}>
      <IconButton
        aria-label={
          (isExpanded
            ? t("Collapse {{criterion}}", { criterion: criterionName })
            : t("Expand {{criterion}}", { criterion: criterionName })) ?? ""
        }
        aria-expanded={isExpanded}
        data-toggle-glyph={toggleGlyph}
        icon={toggleIcon}
        variant="ghost"
        size="xs"
        flexShrink={0}
        onClick={onToggleExpanded}
      />
      {priority != null && !inOrderOfPriority && (
        <Text
          data-testid="award-criterion-priority"
          fontSize="sm"
          fontWeight="semibold"
          color="smMuted"
          minW="5"
          flexShrink={0}
          sx={{ fontVariantNumeric: "tabular-nums" }}
        >
          {priority}.
        </Text>
      )}
      <Badge
        bg="smBackgroundSecondary"
        color="smMuted"
        px="2"
        py="1"
        borderRadius="md"
        textTransform="none"
        fontSize="2xs"
        flexShrink={0}
      >
        {t(criterionTypeLabelKey(criterion.type))}
      </Badge>
      {stacked ? (
        <AutoResizeTextarea
          {...nameFieldProps}
          rows={1}
          minH="0"
          py="1"
          onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
          onChange={(e) => changeName(e.target.value.replace(LINE_BREAKS, " "))}
        />
      ) : (
        <Input
          {...nameFieldProps}
          minW="14rem"
          onChange={(e) => changeName(e.target.value)}
        />
      )}
    </Flex>
  );

  const weightCell = inOrderOfPriority ? (
    <Text fontSize="sm" fontWeight="semibold" whiteSpace="nowrap" data-testid="award-criterion-rank">
      {t("Priority {{rank}}", { rank: priority })}
    </Text>
  ) : (
    <FormControl
      isInvalid={!!weightFieldError}
      w={stacked ? "auto" : undefined}
    >
      <WeightInput
        value={criterion.weight?.number}
        isDisabled={isLocked}
        isInvalid={!!weightFieldError}
        isQuiet
        ariaLabel={t("Weight")}
        onChange={(weight) =>
          onChange({
            ...criterion,
            weight:
              weight == null
                ? undefined
                : { number: weight, type: WeightType.Percentage },
          })
        }
      />
      {weightFieldError && (
        <FormErrorMessage fontSize="xs">
          {formatFieldError(weightFieldError)}
        </FormErrorMessage>
      )}
    </FormControl>
  );

  const actions = (
    <>
      {priority != null && !isLocked && (
        <>
          <IconButton
            aria-label={t("Move {{criterion}} up", { criterion: criterionName }) ?? ""}
            icon={<ArrowUpIcon />}
            variant="ghost"
            size="xs"
            isDisabled={!onMoveUp}
            onClick={onMoveUp}
          />
          <IconButton
            aria-label={t("Move {{criterion}} down", { criterion: criterionName }) ?? ""}
            icon={<ArrowDownIcon />}
            variant="ghost"
            size="xs"
            isDisabled={!onMoveDown}
            onClick={onMoveDown}
          />
        </>
      )}
      {onSaveToLibrary && (
        <IconButton
          aria-label={
            (isInLibrary
              ? t("Already in the requirement library")
              : t("Save to the requirement library")) ?? ""
          }
          icon={<Icon as={FiBookOpen} />}
          variant="ghost"
          size="xs"
          isDisabled={isLocked || isInLibrary}
          onClick={onSaveToLibrary}
        />
      )}
      <IconButton
        aria-label={t("Delete criterion") ?? "Delete"}
        icon={<DeleteIcon />}
        variant="ghost"
        size="xs"
        isDisabled={isLocked}
        onClick={onDelete}
      />
    </>
  );

  if (stacked) {
    return (
      <Box
        data-testid="award-criterion-row"
        px="3"
        py="2"
        bg={isMarked ? "smSuccessSoft" : undefined}
        _hover={{ bg: "smBackgroundSecondary" }}
        {...rowOpenProps(onOpen)}
      >
        {nameCell}
        <Flex align="center" gap="2" wrap="wrap" mt="1" pl="8">
          {weightCell}
          {summaryCell}
          {statusChip}
          <Flex ml="auto">{actions}</Flex>
        </Flex>
      </Box>
    );
  }

  return (
    // The row opens on click, but only where nothing else already owns it: the name is edited inline,
    // the weight is a spinner, and the chevron and the bin each mean their own thing.
    <Tr
      data-testid="award-criterion-row"
      bg={isMarked ? "smSuccessSoft" : undefined}
      // The same hover the app's other clickable rows use, so "this opens something" is one habit.
      _hover={{ bg: "smBackgroundSecondary" }}
      {...rowOpenProps(onOpen)}
    >
      <Td>{nameCell}</Td>
      <Td>{weightCell}</Td>
      <Td>{summaryCell}</Td>
      <Td>{statusChip}</Td>
      <Td textAlign="right" whiteSpace="nowrap">
        {actions}
      </Td>
    </Tr>
  );
};
